import { useEffect, useRef, useState } from "react";
import { api, API, formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Upload, RotateCcw } from "lucide-react";

export default function GalleryManager() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(null);
  const inputRefs = useRef({});

  const load = () => api.get("/gallery").then((r) => setItems(r.data));

  useEffect(() => { load(); }, []);

  const onFile = async (slot, file) => {
    if (!file) return;
    if (!file.type.startsWith("image/")) {
      toast.error("Merci de choisir une image.");
      return;
    }
    if (file.size > 4 * 1024 * 1024) {
      toast.error("Image trop lourde (max 4 Mo).");
      return;
    }
    setBusy(slot);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const res = await fetch(`${API}/admin/gallery/${slot}`, {
        method: "POST",
        credentials: "include",
        body: fd,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(formatErr(err.detail) || "Upload impossible");
      }
      toast.success(`Photo ${slot} mise à jour`);
      await load();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setBusy(null);
    }
  };

  const reset = async (slot) => {
    setBusy(slot);
    try {
      await api.delete(`/admin/gallery/${slot}`);
      toast.success(`Photo ${slot} remise par défaut`);
      await load();
    } catch (e) {
      toast.error(formatErr(e.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="rounded-2xl border border-stone-200 bg-white p-8">
      <div className="mb-6 flex items-baseline justify-between">
        <div>
          <h2 className="font-display text-2xl font-light text-stone-900">Galerie de la landing</h2>
          <p className="mt-1 text-sm text-stone-500">Remplacez chaque photo par un visuel du studio (JPEG/PNG/WebP, max 4 Mo).</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {items.map((g) => (
          <div key={g.slot} data-testid={`gallery-admin-${g.slot}`} className="rounded-xl border border-stone-200 bg-white">
            <div className="relative aspect-[4/5] overflow-hidden rounded-t-xl bg-stone-100">
              <img src={g.src} alt={g.caption} className="h-full w-full object-cover" />
              <span className={`absolute right-3 top-3 rounded-full px-2 py-1 text-[10px] font-medium uppercase tracking-[0.2em] ${g.is_custom ? "bg-[#3b82f6] text-white" : "bg-white/80 text-stone-700"}`}>
                {g.is_custom ? "Perso" : "Défaut"}
              </span>
            </div>
            <div className="space-y-2 p-4">
              <p className="text-xs uppercase tracking-[0.2em] text-stone-500">Photo {g.slot} · {g.caption}</p>
              <input
                ref={(el) => (inputRefs.current[g.slot] = el)}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={(e) => onFile(g.slot, e.target.files?.[0])}
                data-testid={`gallery-file-${g.slot}`}
              />
              <div className="flex gap-2">
                <Button
                  data-testid={`gallery-upload-${g.slot}`}
                  disabled={busy === g.slot}
                  onClick={() => inputRefs.current[g.slot]?.click()}
                  className="flex-1 rounded-full bg-stone-900 text-xs hover:bg-stone-800"
                >
                  <Upload className="mr-2 h-3 w-3" strokeWidth={1.75} />
                  {busy === g.slot ? "…" : g.is_custom ? "Remplacer" : "Uploader"}
                </Button>
                {g.is_custom && (
                  <Button
                    data-testid={`gallery-reset-${g.slot}`}
                    disabled={busy === g.slot}
                    variant="ghost"
                    onClick={() => reset(g.slot)}
                    className="rounded-full border border-stone-200 text-stone-600 hover:text-stone-900"
                  >
                    <RotateCcw className="h-3 w-3" strokeWidth={1.75} />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
